import React, { useCallback } from "react";
import styles from "./styles.module.css";
import { CHART_COLORS } from "~utils/ticker-chart/colorPalette";

const PRIMARY_TICKER_COLOR = "#000000";

function formatPercentValue(value: number): string {
  const sign = value >= 0 ? "+" : "";

  return `${sign}${value.toFixed(2)}%`;
}

type ComparisonTickerPillsProps = {
  readonly primaryTicker?: string;
  readonly tickers: readonly string[];
  readonly onRemove: (tickerId: string) => void;
  readonly values?: Readonly<Record<string, number | null>>;
};

export default function ComparisonTickerPills({
  primaryTicker,
  tickers,
  onRemove,
  values,
}: ComparisonTickerPillsProps) {
  const hasPills = primaryTicker || tickers.length > 0;

  if (!hasPills) {
    return null;
  }

  return (
    <ul className={styles.pillsRow} aria-label="Comparison tickers">
      {primaryTicker && (
        <PrimaryPill ticker={primaryTicker} value={values?.[primaryTicker]} />
      )}
      {tickers.map((ticker, index) => (
        <ComparisonPill
          key={ticker}
          ticker={ticker}
          color={CHART_COLORS[(index + 1) % CHART_COLORS.length]}
          onRemove={onRemove}
          value={values?.[ticker]}
        />
      ))}
    </ul>
  );
}

type PrimaryPillProps = {
  readonly ticker: string;
  readonly value?: number | null;
};

function PrimaryPill({ ticker, value }: PrimaryPillProps) {
  return (
    <li className={styles.pill}>
      <span
        className={styles.colorDot}
        style={{ backgroundColor: PRIMARY_TICKER_COLOR }}
        data-testid="pill-color-dot"
      />
      <span className={styles.tickerLabel}>{ticker}</span>
      {value !== null && value !== undefined && (
        <span className={styles.percentValue} data-testid="pill-percent-value">
          {formatPercentValue(value)}
        </span>
      )}
    </li>
  );
}

type ComparisonPillProps = {
  readonly ticker: string;
  readonly color: string;
  readonly onRemove: (tickerId: string) => void;
  readonly value?: number | null;
};

function ComparisonPill({
  ticker,
  color,
  onRemove,
  value,
}: ComparisonPillProps) {
  const handleRemove = useCallback(() => {
    onRemove(ticker);
  }, [onRemove, ticker]);

  return (
    <li className={styles.pill}>
      <span
        className={styles.colorDot}
        style={{ backgroundColor: color }}
        data-testid="pill-color-dot"
      />
      <span className={styles.tickerLabel}>{ticker}</span>
      {value !== null && value !== undefined && (
        <span className={styles.percentValue} data-testid="pill-percent-value">
          {formatPercentValue(value)}
        </span>
      )}
      <button
        className={styles.removeButton}
        onClick={handleRemove}
        aria-label={`Remove ${ticker}`}
        type="button"
      >
        ×
      </button>
    </li>
  );
}
